import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';
import { ApiRoutes } from '@condo/contracts';
import type {
  Listing,
  ListingsResponse,
  CreateListingRequest,
  UpdateListingRequest,
  AiAnalysisResponse,
  SellerProfile,
  SellerRating,
  ChatThread,
  ChatMessage,
  CreateThreadRequest,
  SendMessageRequest,
  CreateRatingRequest,
} from '@condo/contracts';

/* ------------------------------------------------------------------ */
/*  Listings                                                           */
/* ------------------------------------------------------------------ */

export function useListings(params?: { category?: string }) {
  const category = params?.category;
  const path = category && category !== 'todos'
    ? `${ApiRoutes.mercadoListings}?category=${category}`
    : ApiRoutes.mercadoListings;

  return useQuery({
    queryKey: queryKeys.mercadoListings(category),
    queryFn: () => api.get<ListingsResponse>(path),
  });
}

export function useListing(id: string) {
  return useQuery({
    queryKey: queryKeys.mercadoListing(id),
    queryFn: () => api.get<Listing>(ApiRoutes.mercadoListingById(id)),
    enabled: !!id,
  });
}

export function useMyListings() {
  return useQuery({
    queryKey: queryKeys.mercadoMyListings(),
    queryFn: () => api.get<ListingsResponse>(ApiRoutes.mercadoMyListings),
  });
}

export function useCreateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateListingRequest) =>
      api.post<Listing, CreateListingRequest>(ApiRoutes.mercadoListings, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mercado', 'listings'] });
    },
  });
}

export function useUpdateListing(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateListingRequest) =>
      api.patch<Listing, UpdateListingRequest>(ApiRoutes.mercadoListingById(id), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mercado', 'listings'] });
      void qc.invalidateQueries({ queryKey: queryKeys.mercadoListing(id) });
    },
  });
}

/* ------------------------------------------------------------------ */
/*  AI Analysis                                                        */
/* ------------------------------------------------------------------ */

export function useAnalyzePhotos() {
  return useMutation({
    mutationFn: (photoKeys: string[]) =>
      api.post<AiAnalysisResponse, { photoKeys: string[] }>(
        ApiRoutes.mercadoAnalyzePhotos,
        { photoKeys },
      ),
  });
}

/* ------------------------------------------------------------------ */
/*  Seller Profile & Ratings                                           */
/* ------------------------------------------------------------------ */

export function useSellerProfile(sellerId: string) {
  return useQuery({
    queryKey: queryKeys.mercadoSeller(sellerId),
    queryFn: () => api.get<SellerProfile>(ApiRoutes.mercadoSellerProfile(sellerId)),
    enabled: !!sellerId,
  });
}

export function useSellerRatings(sellerId: string) {
  return useQuery({
    queryKey: queryKeys.mercadoSellerRatings(sellerId),
    queryFn: () =>
      api.get<{ items: SellerRating[] }>(ApiRoutes.mercadoSellerRatings(sellerId)),
    enabled: !!sellerId,
  });
}

export function useCreateRating(sellerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRatingRequest) =>
      api.post<SellerRating, CreateRatingRequest>(
        ApiRoutes.mercadoSellerRatings(sellerId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mercadoSeller(sellerId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.mercadoSellerRatings(sellerId),
      });
    },
  });
}

/* ------------------------------------------------------------------ */
/*  Chat                                                               */
/* ------------------------------------------------------------------ */

export function useChatThreads() {
  return useQuery({
    queryKey: queryKeys.mercadoThreads(),
    queryFn: () => api.get<{ items: ChatThread[] }>(ApiRoutes.mercadoThreads),
  });
}

export function useChatMessages(threadId: string) {
  return useQuery({
    queryKey: queryKeys.mercadoMessages(threadId),
    queryFn: () =>
      api.get<{ items: ChatMessage[] }>(ApiRoutes.mercadoThreadMessages(threadId)),
    enabled: !!threadId,
    refetchInterval: 5000, // Poll every 5 seconds when chat is open
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateThreadRequest) =>
      api.post<ChatThread, CreateThreadRequest>(ApiRoutes.mercadoThreads, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mercadoThreads() });
    },
  });
}

export function useSendMessage(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMessageRequest) =>
      api.post<ChatMessage, SendMessageRequest>(
        ApiRoutes.mercadoThreadMessages(threadId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mercadoMessages(threadId) });
      void qc.invalidateQueries({ queryKey: queryKeys.mercadoThreads() });
    },
  });
}
